import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { useUserRole } from "../../hooks/useUserRole";
import { useGetRecommendationsQuery } from "../../store/apis/controlApi";
import { useGetCompaniesQuery } from "../../store/apis/companyApi";
import {
  ApplyStatus,
  Direction,
  RecommendationStatus,
} from "../../types/control";
import { formatCurrency } from "../../utils/formatUtils";
import {
  ApplyStatusChip,
  directionColor,
  RecommendationStatusChip,
} from "../../components/control/controlPresentation";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

// Estados no-PENDING (el histórico filtra siempre por estado distinto de pendiente).
const HISTORY_STATUSES: RecommendationStatus[] = [
  "ACCEPTED",
  "REJECTED",
  "EXPIRED",
];
const DIRECTIONS: Direction[] = ["INCREASE", "DECREASE"];
const APPLY_STATUSES: ApplyStatus[] = [
  "NOT_APPLIED",
  "APPLIED",
  "FAILED",
  "MANUAL_REQUIRED",
];

const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("es-ES") : "—";

const ControlHistorial: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { t } = useTranslation();
  const { role } = useUserRole();

  // ---- Filtros ----
  const [status, setStatus] = useState<RecommendationStatus | "">("");
  const [direction, setDirection] = useState<Direction | "">("");
  const [applyStatus, setApplyStatus] = useState<ApplyStatus | "">("");
  const [companyId, setCompanyId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Paginación (MUI 0-indexed; backend 1-indexed).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Debounce de la búsqueda de texto (~400ms).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: companiesResp } = useGetCompaniesQuery();
  const companies = useMemo(() => companiesResp?.data ?? [], [companiesResp]);

  const { data: resp, isFetching } = useGetRecommendationsQuery({
    page: page + 1,
    pageSize,
    // Si no se filtra por un estado concreto, el backend devuelve los no-PENDING
    // cuando se omite `status`; aquí solo enviamos estados del histórico.
    status: (status || undefined) as RecommendationStatus | undefined,
    direction: direction || undefined,
    applyStatus: applyStatus || undefined,
    companyId: companyId || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    search: search || undefined,
  });

  if (role && !["ADMIN", "STRATEGY", "TECHNICIAN", "PRICING"].includes(role)) {
    return <Navigate to="/" replace />;
  }

  const items = (resp?.data?.items ?? []).filter((r) => r.status !== "PENDING");
  const total = resp?.data?.total ?? 0;

  const clearFilters = () => {
    setStatus("");
    setDirection("");
    setApplyStatus("");
    setCompanyId("");
    setStartDate("");
    setEndDate("");
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const hasFilters =
    status ||
    direction ||
    applyStatus ||
    companyId ||
    startDate ||
    endDate ||
    search;

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h3">{t("control.historyPage.title")}</Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/control")}
            sx={{ textTransform: "none" }}
          >
            {t("control.historyPage.pending")}
          </Button>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("control.historyPage.filterStatus")}
                fullWidth
                size="small"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as RecommendationStatus | "");
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("control.historyPage.all")}</MenuItem>
                {HISTORY_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`control.status.${s}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("control.historyPage.filterDirection")}
                fullWidth
                size="small"
                value={direction}
                onChange={(e) => {
                  setDirection(e.target.value as Direction | "");
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("control.historyPage.allFem")}</MenuItem>
                {DIRECTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`control.direction.${d}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("control.historyPage.filterApply")}
                fullWidth
                size="small"
                value={applyStatus}
                onChange={(e) => {
                  setApplyStatus(e.target.value as ApplyStatus | "");
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("control.historyPage.all")}</MenuItem>
                {APPLY_STATUSES.map((a) => (
                  <MenuItem key={a} value={a}>
                    {t(`control.applyStatus.${a}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("control.historyPage.filterClient")}
                fullWidth
                size="small"
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("control.historyPage.all")}</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nameCommercial || c.nameFiscal || c.id}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("control.historyPage.search")}
                fullWidth
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("control.historyPage.from")}
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("control.historyPage.to")}
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            {hasFilters && (
              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Button onClick={clearFilters} sx={{ textTransform: "none" }}>
                  {t("control.historyPage.clear")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Tabla */}
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      bgcolor: theme.palette.neutral["100"],
                    },
                  }}
                >
                  <TableCell>{t("control.historyPage.colLine")}</TableCell>
                  <TableCell>{t("control.historyPage.colClient")}</TableCell>
                  <TableCell>{t("control.historyPage.colDirection")}</TableCell>
                  <TableCell align="right">
                    {t("control.historyPage.colCurrentSuggested")}
                  </TableCell>
                  <TableCell align="right">
                    {t("control.historyPage.colVariation")}
                  </TableCell>
                  <TableCell>{t("control.historyPage.colStatus")}</TableCell>
                  <TableCell>{t("control.historyPage.colApply")}</TableCell>
                  <TableCell>{t("control.historyPage.colDecidedBy")}</TableCell>
                  <TableCell>{t("control.historyPage.colDate")}</TableCell>
                  <TableCell>{t("control.historyPage.colNote")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color={theme.palette.neutral["600"]}>
                        {t("control.historyPage.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2">{r.lineName}</Typography>
                        <Typography
                          variant="caption"
                          color={theme.palette.neutral[600]}
                        >
                          {r.insertionOrderName}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.companyName}</TableCell>
                      <TableCell
                        sx={{
                          color: directionColor(theme)[r.direction],
                          fontWeight: 700,
                        }}
                      >
                        {t(`control.direction.${r.direction}`)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(r.currentInvestment, r.currency)} →{" "}
                        {formatCurrency(r.suggestedInvestment, r.currency)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: directionColor(theme)[r.direction],
                          fontWeight: 700,
                        }}
                      >
                        {r.variationPercent > 0 ? "+" : ""}
                        {r.variationPercent}%
                      </TableCell>
                      <TableCell>
                        <RecommendationStatusChip status={r.status} />
                      </TableCell>
                      <TableCell>
                        <ApplyStatusChip applyStatus={r.applyStatus} />
                      </TableCell>
                      <TableCell>{r.decidedByName || "—"}</TableCell>
                      <TableCell>{fmtDateTime(r.decidedAt)}</TableCell>
                      <TableCell>
                        {r.decisionNote ? (
                          <Tooltip title={r.decisionNote}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "inline-block",
                                maxWidth: 180,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.decisionNote}
                            </Typography>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t("common.rowsPerPage")}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default ControlHistorial;
